/**
 * Brand and structural constants for the landing page.
 *
 * Display copy lives in the i18n message files (messages/*.json) and is read
 * via next-intl. This module holds only non-translated structure: the company
 * name (a proper noun) and the offering/package key sets that index the
 * message namespaces and illustration maps.
 *
 * The company name itself lives in the project-wide company.config.mjs so it
 * stays in sync with the PDF-generation scripts.
 */
import { COMPANY_NAME } from "../../company.config.mjs";

export const brand = {
  name: COMPANY_NAME,
} as const;

/** Offering keys — also used as the message key for each card. */
const offeringKeys = ["website", "ios"] as const;

type OfferingKey = (typeof offeringKeys)[number];

/**
 * Package keys in pricing-card display order: the offerings plus Custom.
 * The single source of truth for the pricing selection and onboarding grounding.
 */
export const packageKeys = [...offeringKeys, "custom"] as const;

export type PackageKey = (typeof packageKeys)[number];

/**
 * File-name stem for each offering's generated PDFs in /public. Intake forms
 * live at /intake/<stem>-intake.pdf and sample agreements at
 * /contracts/<stem>-contract.pdf (see scripts/generate-*.mjs).
 */
export const offeringDocStem: Record<OfferingKey, string> = {
  website: "web-apps",
  ios: "ios-apps",
} as const;

/** Static path to the generated price sheet PDF in /public. */
export const PRICE_SHEET_HREF = "/pricing/price-sheet.pdf";

/** Process-timeline step keys, in chronological order. */
export const processStepKeys = ["scope", "build", "ship"] as const;

/** "What you get" deliverable card keys, in handover order. */
export const deliverableKeys = ["app", "infra", "handoff"] as const;

export type DeliverableKey = (typeof deliverableKeys)[number];

/**
 * Footer navigation structure. Column and link keys map to labels in the
 * `footerNav` message namespace; hrefs point to on-page anchors where a
 * section exists, or "#" placeholders for pages not yet built.
 */
export interface FooterLink {
  readonly key: string;
  readonly href: string;
}

export interface FooterColumn {
  readonly key: string;
  readonly links: readonly FooterLink[];
}

export const footerColumns: readonly FooterColumn[] = [
  {
    key: "services",
    links: [
      { key: "website", href: "/#services" },
      { key: "ios", href: "/#services" },
    ],
  },
  {
    key: "company",
    links: [
      { key: "work", href: "/work" },
      { key: "about", href: "/about" },
      { key: "process", href: "/#process" },
      { key: "start", href: "/start" },
    ],
  },
  {
    key: "resources",
    links: [
      { key: "faq", href: "/faq" },
      { key: "press", href: "/press" },
      { key: "pricesheet", href: PRICE_SHEET_HREF },
    ],
  },
  {
    key: "legal",
    links: [
      { key: "privacy", href: "/legal/privacy" },
      { key: "terms", href: "/legal/terms" },
      { key: "gdpr", href: "/legal/gdpr" },
    ],
  },
] as const;
